import {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import ModalService from '../services/ModalService';
import { dbPost } from '../apis/commonApi';
export const GlobalContext = createContext(null);

export const GlobalProvider = ({ children }) => {
  const { modalService } = useContext(ModalContext);

  // const SESSION_TIMEOUT = 60 * 60 * 1000;
  const SESSION_TIMEOUT = 3 * 60 * 60 * 1000;
  // const SESSION_TIMEOUT = 1 * 60 * 1000;
  // const INACTIVITY_WARNING_TIME = 30 * 60 * 1000;
  const INACTIVITY_WARNING_TIME = 3 * 60 * 60 * 1000;
  // const INACTIVITY_WARNING_TIME = 3 * 60 * 1000;
  const REMAINING_WARNING_TIME = 10 * 60 * 1000;
  const INACTIVITY_LOGOUT_TIME = 5 * 60 * 1000;

  const [store, setStore] = useState(() => {
    const savedStore = sessionStorage.getItem('appStore');
    return savedStore
      ? JSON.parse(savedStore)
      : {
          logined: false,
          userType: '',
          entSn: '',
          userSn: '',
          userId: '',
          userNm: '',
          authrtId: 'NU',
          selectedFirstDepthMenu: '',
          codeList: [],
          menuList: [],
        };
  });

  const getSavedTime = () => {
    const savedTime = sessionStorage.getItem('remainingTime');
    return savedTime ? parseInt(savedTime, 10) : SESSION_TIMEOUT;
  };
  const [remainingTime, setRemainingTime] = useState(getSavedTime);
  const sessionTimerRef = useRef(null);
  const intervalRef = useRef(null);
  const lastVisibilityChangeRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const inactivityTimeRef = useRef(0);
  const warnedRef = useRef(false);
  const prevRemainingTimeRef = useRef(remainingTime);

  useEffect(() => {
    const savedTime = getSavedTime();
    setRemainingTime(savedTime);
  }, []);

  useEffect(() => {
    if (!store.logined) return;

    let lastUpdate = Date.now();

    const tick = () => {
      clearTimeout(intervalRef.current);
      const now = Date.now();
      const elapsed = now - lastUpdate;
      lastUpdate = now;

      setRemainingTime(prev => {
        const newTime = Math.max(prev - elapsed, 0);
        sessionStorage.setItem('remainingTime', newTime);
        return newTime;
      });

      if (remainingTime > 0) {
        intervalRef.current = setTimeout(tick, 1000);
      }
    };

    tick();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        lastVisibilityChangeRef.current = Date.now();
      } else {
        const now = Date.now();
        const hiddenDuration = now - (lastVisibilityChangeRef.current || now);

        setRemainingTime(prev => {
          if (hiddenDuration > 0 && hiddenDuration < SESSION_TIMEOUT) {
            return Math.max(prev - hiddenDuration, 0);
          }
          return prev;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [store.logined]);

  const showSessionWarning = useCallback(() => {
    if (
      !warnedRef.current &&
      remainingTime <= REMAINING_WARNING_TIME &&
      store.logined
    ) {
      warnedRef.current = true;
      modalService.confirmAlert(
        { cnt: '세션 만료가 10분 남았습니다. 로그인 시간을 연장하시겠습니까?' },
        [
          () => {
            warnedRef.current = false;
            resetTimer(true);
          },
          () => {},
        ]
      );
    }
  }, [remainingTime, modalService]);

  useEffect(() => {
    showSessionWarning();
    prevRemainingTimeRef.current = remainingTime;
  }, [remainingTime, showSessionWarning]);

  useEffect(() => {
    if (store) {
      sessionStorage.setItem('appStore', JSON.stringify(store));
    }
  }, [store]);

  const logoutUser = message => {
    clearTimeout(sessionTimerRef.current);
    clearTimeout(inactivityTimerRef.current);
    clearInterval(intervalRef.current);

    localStorage.removeItem('remainingTime');
    sessionStorage.removeItem('remainingTime');
    setRemainingTime(0);

    sessionStorage.clear();
    setStore(prev => ({ ...prev, logined: false }));

    if (message) {
      alert(message);
    }

    window.location.href = '/member/login';
  };

  const resetTimer = async (shouldRenew = true) => {
    clearTimeout(sessionTimerRef.current);
    clearTimeout(inactivityTimerRef.current);
    clearInterval(intervalRef.current);

    const savedTime = getSavedTime();
    const newTime = shouldRenew ? SESSION_TIMEOUT : savedTime;

    setRemainingTime(newTime);
    sessionStorage.setItem('remainingTime', newTime);

    warnedRef.current = false;
    inactivityTimeRef.current = 0;

    if (shouldRenew) {
      try {
        await renewToken();
        setRemainingTime(SESSION_TIMEOUT);
        sessionStorage.setItem('remainingTime', SESSION_TIMEOUT);
      } catch (error) {
        console.error('Token renewal failed', error);
        logoutUser('세션 갱신 실패로 로그아웃 되었습니다.');
      }
    }

    clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(
      () => logoutUser('세션 만료로 자동 로그아웃 되었습니다.'),
      newTime
    );

    intervalRef.current = setInterval(() => {
      setRemainingTime(prev => {
        const newTime = prev > 1000 ? prev - 1000 : 0;
        sessionStorage.setItem('remainingTime', newTime);
        return newTime;
      });
    }, 1000);

    inactivityTimerRef.current = setInterval(() => {
      inactivityTimeRef.current += 1000;

      if (
        inactivityTimeRef.current >= INACTIVITY_WARNING_TIME &&
        !warnedRef.current
      ) {
        warnedRef.current = true;
        const autoLogoutTimer = setTimeout(() => {
          logoutUser('장시간 미사용으로 자동 로그아웃 되었습니다.');
        }, INACTIVITY_LOGOUT_TIME);
        modalService.confirmAlert(
          {
            cnt: '장시간 미사용으로 인해 5분 뒤 로그아웃 됩니다. 로그인 세션을 연장하시겠습니까?',
          },
          [
            () => {
              clearTimeout(autoLogoutTimer);
              resetTimer(true);
            },
            () => {
              clearTimeout(autoLogoutTimer);
              logoutUser('자동 로그아웃 되었습니다.');
            },
          ]
        );
      }
    }, 1000);
  };

  const renewToken = async () => {
    const token = sessionStorage.getItem('token');
    if (token) {
      const url = '/common/renewToken.do';
      await dbPost(url, { token });
    }
  };

  const updateStore = (key, value) => {
    setStore(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <GlobalContext.Provider
      value={{ store, updateStore, remainingTime, resetTimer }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
export const useGlobalStore = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalStore must be used within a GlobalProvider');
  }
  return context;
};

export const LoadingContext = createContext(null);

export const AuthContext = createContext(null);
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  const login = async credentials => {
    try {
      const response = await doLogin(credentials);

      if (response.logined) {
        setUser(response.user);
      }
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.clear();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const BreadcrumbContext = createContext();

export const BreadcrumbProvider = ({ children }) => {
  const [breadcrumb, setBreadcrumb] = useState(['홈']);

  return (
    <BreadcrumbContext.Provider value={{ breadcrumb, setBreadcrumb }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

// **추가된 부분**
export const useBreadcrumb = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
};

export const ModalContext = createContext(null);
export const ModalProvider = ({ children }) => {
  const [modalService] = useState(new ModalService());
  const [modalList, setModalList] = useState(modalService.modalList);

  useEffect(() => {
    const handleModalListChange = newModalList => {
      setModalList([...newModalList]);
    };

    modalService.subscribe(handleModalListChange);
    return () => {
      modalService.unsubscribe(handleModalListChange);
    };
  }, [modalService]);

  return (
    <ModalContext.Provider value={{ modalService, modalList }}>
      {children}
    </ModalContext.Provider>
  );
};
